<template>
  <div
    v-tooltip.top="{ value: !appWidget.installed ? t('templateBuilder.panels.widgets.appWidgetTooltip') : '', disabled: appWidget.installed }"
    class="app-widget-btn"
    :class="{ 'app-widget-btn--locked': !appWidget.installed }"
  >
    <div class="app-widget-btn__icon">
      <img v-if="appWidget.iconImage" :src="appWidget.iconImage" alt="" class="app-widget-btn__icon-img" />
      <em v-else :class="appWidget.iconClass" />
    </div>
    <span class="app-widget-btn__label">{{ appWidget.label }}</span>
    <span
      v-if="!compact"
      class="app-widget-btn__badge"
      :class="appWidget.installed ? 'app-widget-btn__badge--installed' : 'app-widget-btn__badge--locked'"
    >
      <em :class="appWidget.installed ? 'far fa-check' : 'far fa-lock'" />
      {{ appWidget.installed
        ? t('templateBuilder.panels.widgets.appInstalled')
        : t('templateBuilder.panels.widgets.appNotInstalled')
      }}
    </span>
    <span v-if="!compact" class="app-widget-btn__app-name">{{ appWidget.appName }}</span>
    <a
      v-if="!compact && !appWidget.installed"
      class="app-widget-btn__install-link"
      href="#"
      @click.prevent="$emit('install', appWidget)"
    >
      {{ t('templateBuilder.panels.widgets.appInstallCta') }}
      <em class="far fa-external-link" />
    </a>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import type { IntegrationKind } from '@/types/integrations';

export interface AppWidget {
  id: string;
  type?: string;
  group?: string;
  label: string;
  appName: string;
  iconClass?: string;
  iconImage?: string;
  installed: boolean;
  marketplaceUrl?: string;
  // Integration widgets: identify the concrete widget at drop / config time.
  integrationSlug?: string;
  widgetKey?: string;
  kind?: IntegrationKind;
}

export default defineComponent({
  name: 'AppWidgetButton',

  directives: {
    tooltip: Tooltip,
  },

  props: {
    appWidget: {
      type: Object as () => AppWidget,
      required: true,
    },
    // Native-look variant (first-party widgets listed among native widgets):
    // hide the install badge / app subtitle / install link → icon + label only.
    compact: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['install'],

  setup() {
    const { t } = useI18n();
    return { t };
  },
});
</script>

<style lang="scss" scoped>
.app-widget-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 6px 8px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e9ecef;
  cursor: grab;
  transition: all 0.2s ease;
  position: relative;
  height: 100%;

  &:active:not(.app-widget-btn--locked) {
    cursor: grabbing;
    transform: scale(0.95);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    transition: all 0.2s ease;
    flex-shrink: 0;

    em {
      font-size: 1.25rem;
      color: #6366f1;
      transition: color 0.2s ease;
    }
  }

  &__icon-img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    text-align: center;
    color: #495057;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }

  &__app-name {
    font-size: 9px;
    color: #9ca3af;
    font-weight: 400;
    text-align: center;
    line-height: 1;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 9px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 10px;
    line-height: 16px;

    em {
      font-size: 8px;
    }

    &--installed {
      background: rgba(148, 200, 64, 0.12);
      color: #6a9a20;
    }

    &--locked {
      background: rgba(107, 114, 128, 0.1);
      color: #9ca3af;
    }
  }

  &__install-link {
    display: none;
    font-size: 10px;
    font-weight: 600;
    color: #6366f1;
    text-decoration: none;
    gap: 3px;
    align-items: center;
    margin-top: 2px;
    transition: color 0.15s ease;

    em {
      font-size: 9px;
    }

    &:hover {
      color: #4f46e5;
      text-decoration: underline;
    }
  }

  &:hover:not(.app-widget-btn--locked) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: rgba(99, 102, 241, 0.3);

    .app-widget-btn__icon em {
      color: #4f46e5;
    }
  }

  &--locked {
    opacity: 0.65;
    cursor: default;
    background: #fafafa;

    &:hover {
      opacity: 0.85;
      border-color: #d1d5db;

      .app-widget-btn__badge {
        display: none;
      }

      .app-widget-btn__install-link {
        display: inline-flex;
      }
    }
  }
}
</style>
